import SiteSVG from "assets/svg/Site.svg";
import MainPowerBoardSVG from "assets/svg/MainPowerBoard.svg";
import DistributionBoardSVG from "assets/svg/DB.svg";
import CircuitSVG from "assets/svg/Charging.svg";
import GidSVG from "assets/svg/GID.svg";
import ConnectedSVG from "assets/svg/Connected.svg";
import NotConnectedSVG from "assets/svg/NotConnected.svg";

import getChargerGraphic from "./getChargerGraphic";

type RichStyles = {
  height?: number | string;
  width?: number | string;
  lineHeight?: number;
  color?: string;
  align?: string;
  fontSize?: string | number;
  fontWeight?: string;
  padding?: number[];
  backgroundColor?: { image?: string };
  verticalAlign?: string;
};

export type SiteTreeChildren = {
  name: string;
  value: string;
  symbol?: string;
  symbolSize?: number | number[];
  symbolKeepAspect?: boolean;
  label?: {
    formatter: string;
    offset?: number[];
    position?: string;
    rich: Record<string, RichStyles>;
  };
  itemStyle?: Record<string, unknown>;
  lineStyle?: Record<string, unknown>;
  select?: Record<string, unknown>;
  children: SiteTreeChildren[];
};

type SiteTreeNodeOptions = {
  nodeType: string;
  nodeLabel: string | null;
  hasCurrentMeter?: boolean;
  powerTagLabel?: string;
  deviceModel?: string;
  nodeDescription?: string;
  isOnline?: boolean;
  isCharging?: boolean;
  isSmallToMediumScreen?: boolean;
};

export default function getSiteTreeNodeOptions({
  nodeType,
  nodeLabel,
  hasCurrentMeter = false,
  powerTagLabel,
  deviceModel,
  nodeDescription,
  isOnline = false,
  isCharging = false,
  isSmallToMediumScreen = false,
}: SiteTreeNodeOptions): SiteTreeChildren {
  const pillShapePath = "path://M153.2,1.1H23.3c-12.3,0-22.2,9-22.2,20s9.9,20,22.2,20h129.8c12.3,0,22.2-9,22.2-20S165.4,1.1,153.2,1.1z";

  switch (nodeType) {
  case "site":
    return {
      name: nodeLabel ?? "Site",
      select: { itemStyle: { borderColor: "#001DD2" } },
      value: "site",
      label: {
        formatter: `{name|${nodeLabel}}\n \n {graphic|}`,
        offset: [0, -18],
        rich: { graphic: { width: 40, height: 40, backgroundColor: { image: SiteSVG } } },
      },
      children: [],
    };
  case "mainDistBoard":
    return hasCurrentMeter
      ? {
        name: nodeLabel ?? "Main Board",
        value: "mainDistBoard",
        itemStyle: { borderColor: "#CBCBCB" },
        select: { itemStyle: { borderColor: "#001DD2" } },
        label: {
          formatter: `{name|${nodeLabel}}\n {graphic|}\n \n ${powerTagLabel != null ? `{tag|} {tagText|${powerTagLabel}` : "{tag|}"}\n {description|${nodeDescription}}`,
          offset: [-1, 34],
          rich: {
            name: { color: "#1a1a1a", fontWeight: "bold", align: "center", padding: [0, -1, 10, 0] },
            description: { color: "#666666", align: "center", padding: [0, -1, 0, 0] },
            graphic: { align: "center", width: 40, height: 60, backgroundColor: { image: MainPowerBoardSVG } },
            tag: { align: "center", width: 40, height: 40, backgroundColor: { image: GidSVG } },
            tagText: { align: "center", color: "#1a1a1a", fontWeight: "bold", fontSize: 14, width: 60, height: 40 },
          },
        },
        children: [],
      }
      : {
        name: nodeLabel ?? "Main Board",
        value: "mainDistBoard",
        itemStyle: { borderColor: "#CBCBCB" },
        select: { itemStyle: { borderColor: "#001DD2" } },
        label: {
          formatter: `{name|${nodeLabel}}\n {graphic|} \n {description|${nodeDescription}}`,
          offset: [-1, 8],
          rich: {
            name: { color: "#1a1a1a", fontWeight: "bold", align: "center", padding: [0, -1, 10, 0] },
            description: { color: "#666666", align: "center", padding: [0, -1, 0, 0] },
            graphic: { align: "center", width: 40, height: 60, backgroundColor: { image: MainPowerBoardSVG } },
          },
        },
        children: [],
      };
  case "subDistBoard":
    return hasCurrentMeter
      ? {
        name: nodeLabel ?? "Sub Board",
        value: "subDistBoard",
        itemStyle: { borderColor: "#CBCBCB" },
        select: { itemStyle: { borderColor: "#001DD2" } },
        label: {
          formatter: `{name|${nodeLabel}}\n {graphic|}\n \n ${powerTagLabel != null ? `{tag|} {tagText|${powerTagLabel}` : "{tag|}"}\n {description|${nodeDescription}}`,
          offset: [1, 32],
          rich: {
            name: { color: "#1a1a1a", fontWeight: "bold", align: "center", padding: [0, 1, 21, 0] },
            description: { padding: [0, 1, 0, 0], color: "#666666", align: "center" },
            graphic: { align: "center", width: 80, height: 40, backgroundColor: { image: DistributionBoardSVG } },
            tag: { align: "center", width: 40, height: 40, backgroundColor: { image: GidSVG } },
            tagText: { align: "center", color: "#1a1a1a", fontWeight: "bold", fontSize: 14, width: 60, height: 40 },
          },
        },
        children: [],
      }
      : {
        name: nodeLabel ?? "Sub Board",
        value: "subDistBoard",
        itemStyle: { borderColor: "#CBCBCB" },
        select: { itemStyle: { borderColor: "#001DD2" } },
        label: {
          formatter: `{name|${nodeLabel}}\n\n{graphic|} \n{description|${nodeDescription}}`,
          offset: [2, 0],
          rich: {
            name: { color: "#1a1a1a", fontWeight: "bold", align: "center", padding: [0, 2, 10, 0] },
            description: { padding: [20, 2, 0, 0], color: "#666666", align: "center" },
            graphic: { align: "center", width: 80, height: 40, backgroundColor: { image: DistributionBoardSVG } },
          },
        },
        children: [],
      };
  case "circuit":
    return {
      name: nodeLabel ?? "Circuit",
      value: "circuit",
      itemStyle: { borderColor: "#CBCBCB" },
      select: { itemStyle: { borderColor: "#001DD2" } },
      label: {
        formatter: `{name|${nodeLabel}}\n\n{graphic|}`,
        offset: [0, -18],
        rich: {
          name: { color: "#1a1a1a", fontWeight: "bold", align: "center", padding: [0, 0, 7, 0] },
          graphic: { align: "center", width: 80, height: 40, backgroundColor: { image: CircuitSVG } },
        },
      },
      children: [],
    };
  case "charger":
    if (isSmallToMediumScreen) {
      return {
        name: nodeLabel ?? "Charger",
        value: "charger",
        itemStyle: { borderColor: "#CBCBCB" },
        select: { itemStyle: { borderColor: "#001DD2" } },
        lineStyle: { color: isCharging ? "#001DD2" : "#666666" },
        label: {
          formatter: `{icon|}\n{graphic|}\n\n{name|${nodeLabel}}\n{ampLimitText|${nodeDescription}}`,
          offset: [0, 13],
          rich: {
            icon: {
              width: 25,
              height: 25,
              verticalAlign: "center",
              align: "center",
              padding: [-5, 0, 0, 0],
              backgroundColor: { image: isOnline ? ConnectedSVG : NotConnectedSVG },
            },
            graphic: {
              align: "center",
              width: 40,
              height: 40,
              padding: [12, 0, 0, 0],
              verticalAlign: "center",
              backgroundColor: { image: getChargerGraphic(deviceModel ?? "") },
            },
            name: { color: "#1a1a1a", fontWeight: "bold", align: "center", verticalAlign: "center", padding: [3, 5, 0, 5] },
            ampLimitText: { color: "#1a1a1a", padding: [5, 0, 0, 0], align: "center", verticalAlign: "center" },
          },
        },
        children: [],
      };
    }
    return {
      name: nodeLabel ?? "Charger",
      value: "charger",
      symbol: pillShapePath,
      symbolSize: 300,
      symbolKeepAspect: true,
      itemStyle: { borderColor: "#CBCBCB" },
      select: { itemStyle: { borderColor: "#001DD2" } },
      lineStyle: { width: isCharging ? 2 : 1.5, color: isCharging ? "#001DD2" : "#666666" },
      label: {
        formatter: `{icon|} {graphic|} {name|${(nodeLabel?.length ?? 0) > 12 ? nodeLabel?.slice(0, 12) + "..." : nodeLabel}}\t\t\t{ampLimitText|${nodeDescription}}`,
        offset: [-20, 0],
        position: "insideLeft",
        rich: {
          name: { color: "#1a1a1a", fontWeight: "bold", align: "center", verticalAlign: "center", padding: [-1, 1, 0, 3] },
          graphic: {
            align: "center",
            width: 40,
            height: 40,
            verticalAlign: "center",
            backgroundColor: { image: getChargerGraphic(deviceModel ?? "") },
          },
          icon: {
            width: 25,
            height: 25,
            verticalAlign: "center",
            backgroundColor: { image: isOnline ? ConnectedSVG : NotConnectedSVG },
          },
          ampLimitText: { color: "#1a1a1a", align: "center", verticalAlign: "center" },
        },
      },
      children: [],
    };
  default:
    return {
      name: nodeLabel ?? "Node",
      value: "node",
      label: {
        formatter: `{name|${nodeLabel}}\n \n {graphic|}`,
        offset: [0, -18],
        rich: { graphic: { width: 40, height: 40, backgroundColor: { image: SiteSVG } } },
      },
      children: [],
    };
  }
}
